import React, { useEffect, useState } from 'react'
import { Row, Col, Button, Modal, Form, Input, message, Empty, Popconfirm } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, FileTextOutlined } from '@ant-design/icons'
import api from '../../api'
import { useAuth } from '../../store/auth'

export default function HelpPage() {
  const { user } = useAuth()
  const [tree, setTree] = useState<any[]>([])
  const [catModal, setCatModal] = useState(false)
  const [editCat, setEditCat] = useState<{ key: string; title: string } | null>(null)
  const [docModal, setDocModal] = useState<string | null>(null)
  const [docLoading, setDocLoading] = useState(false)
  const [editDoc, setEditDoc] = useState<any>(null)
  const [editDocForm] = Form.useForm()
  const [catForm] = Form.useForm()
  const [docForm] = Form.useForm()

  const loadTree = () => api.get('/help/tree').then((r: any) => setTree(r)).catch(() => {})

  useEffect(() => { loadTree() }, [])

  const handleCreateCat = async () => {
    const values = catForm.getFieldsValue()
    if (!values.title?.trim()) return
    await api.post('/help/categories', { title: values.title.trim() })
    message.success('模块创建成功')
    setCatModal(false)
    catForm.resetFields()
    loadTree()
  }

  const handleRenameCat = async () => {
    if (!editCat) return
    const values = catForm.getFieldsValue()
    if (!values.title?.trim()) return
    await api.patch(`/help/categories/${editCat.key}`, { title: values.title.trim() })
    message.success('已修改')
    setEditCat(null)
    catForm.resetFields()
    loadTree()
  }

  const handleDeleteCat = async (catKey: string) => {
    await api.delete(`/help/categories/${catKey}`)
    message.success('模块已删除')
    loadTree()
  }

  const handleCreateDoc = async (values: any) => {
    setDocLoading(true)
    try {
      await api.post('/help/docs', { ...values, category: docModal })
      message.success('文档已添加')
      setDocModal(null)
      docForm.resetFields()
      loadTree()
    } catch (e: any) {
      const detail = e?.response?.data?.detail || ''
      if (detail.includes('标题')) {
        message.warning('无法自动获取标题，请手动填写文档标题')
      } else {
        message.error('添加失败')
      }
    } finally {
      setDocLoading(false)
    }
  }

  const handleDeleteDoc = async (docId: string) => {
    await api.delete(`/help/docs/${docId}`)
    message.success('文档已删除')
    setEditDoc(null)
    loadTree()
  }

  const handleEditDoc = async () => {
    const values = editDocForm.getFieldsValue()
    if (!values.title?.trim()) return
    await api.patch(`/help/docs/${editDoc.key}`, { title: values.title.trim(), feishu_url: values.feishu_url?.trim() || '' })
    message.success('文档已更新')
    setEditDoc(null)
    loadTree()
  }

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin'

  const categories = tree.map((cat: any) => ({
    key: cat.key,
    title: cat.title,
    docs: cat.children || [],
  }))

  return (
    <div>
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#4A4A4A' }}>常用文档中心</div>
          <div style={{ color: '#B9B9B9', fontSize: 13, marginTop: 4 }}>用户付费组常用文档</div>
        </div>
        {isAdmin && (
          <Button icon={<PlusOutlined />} type="primary" size="small"
            onClick={() => { setCatModal(true); catForm.resetFields() }}>新增模块</Button>
        )}
      </div>

      {categories.length === 0 ? (
        <Empty image={<FileTextOutlined style={{ fontSize: 48, color: '#D8DEFC' }} />}
          description={<span style={{ color: '#B9B9B9' }}>暂无文档模块</span>}
          style={{ paddingTop: 80 }} />
      ) : (
        <Row gutter={[20, 20]}>
          {categories.map(cat => (
            <Col key={cat.key} xs={24} md={12}>
              <div style={{
                background: '#fff', borderRadius: 12, padding: '20px 24px',
                boxShadow: '0 2px 8px #E8EAFC', minHeight: 140,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16, fontWeight: 600, color: '#4A4A4A' }}>{cat.title}</span>
                    {isAdmin && (
                      <EditOutlined
                        style={{ fontSize: 13, color: '#999', cursor: 'pointer' }}
                        onClick={() => { setEditCat({ key: cat.key, title: cat.title }); catForm.setFieldsValue({ title: cat.title }) }}
                      />
                    )}
                    {isAdmin && (
                      <Popconfirm title="确定删除该模块及其所有文档？" onConfirm={() => handleDeleteCat(cat.key)} okText="删除" cancelText="取消">
                        <DeleteOutlined style={{ fontSize: 13, color: '#ff4d4f', cursor: 'pointer' }} />
                      </Popconfirm>
                    )}
                  </div>
                  {isAdmin && (
                    <Button type="link" size="small" icon={<PlusOutlined />}
                      onClick={() => { setDocModal(cat.title); docForm.resetFields() }}>添加文档</Button>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {cat.docs.map((doc: any) => (
                    <div key={doc.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <a href={doc.feishu_url || '#'} target="_blank" rel="noopener noreferrer"
                        style={{ color: '#536DFE', fontSize: 14, flex: 1 }}>
                        {doc.title}
                      </a>
                      {isAdmin && (
                        <EditOutlined
                          style={{ fontSize: 12, color: '#999', cursor: 'pointer' }}
                          onClick={() => { setEditDoc(doc); editDocForm.setFieldsValue({ title: doc.title, feishu_url: doc.feishu_url }) }}
                        />
                      )}
                    </div>
                  ))}
                  {cat.docs.length === 0 && (
                    <span style={{ color: '#ccc', fontSize: 13 }}>暂无文档</span>
                  )}
                </div>
              </div>
            </Col>
          ))}
        </Row>
      )}

      {/* 新增模块 */}
      <Modal title="新增模块" open={catModal} onCancel={() => setCatModal(false)} onOk={handleCreateCat} destroyOnClose>
        <Form form={catForm} layout="vertical">
          <Form.Item name="title" label="模块名称" rules={[{ required: true }]}><Input placeholder="如：运营操作手册" /></Form.Item>
        </Form>
      </Modal>

      {/* 修改模块名称 */}
      <Modal title="修改模块名称" open={!!editCat} onCancel={() => setEditCat(null)} onOk={handleRenameCat} destroyOnClose>
        <Form form={catForm} layout="vertical">
          <Form.Item name="title" label="模块名称" rules={[{ required: true }]}><Input /></Form.Item>
        </Form>
      </Modal>

      {/* 添加文档 */}
      <Modal title={`添加文档到「${docModal}」`} open={!!docModal} onCancel={() => setDocModal(null)}
        confirmLoading={docLoading} onOk={() => docForm.submit()} destroyOnClose>
        <Form form={docForm} onFinish={handleCreateDoc} layout="vertical">
          <Form.Item name="title" label="文档标题" rules={[{ required: true, message: '请填写文档标题' }]}>
            <Input placeholder="如：服务器使用规范手册" />
          </Form.Item>
          <Form.Item name="feishu_url" label="文档链接" rules={[{ required: true, message: '请填写文档链接' }]}>
            <Input placeholder="https://..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑文档 */}
      <Modal title="编辑文档" open={!!editDoc} onCancel={() => setEditDoc(null)} onOk={handleEditDoc} destroyOnClose
        footer={(_, { OkBtn, CancelBtn }) => (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Popconfirm title="确定删除该文档？" onConfirm={() => handleDeleteDoc(editDoc?.key)} okText="删除" cancelText="取消">
              <Button danger>删除文档</Button>
            </Popconfirm>
            <div style={{ display: 'flex', gap: 8 }}>
              <CancelBtn />
              <OkBtn />
            </div>
          </div>
        )}>
        <Form form={editDocForm} layout="vertical">
          <Form.Item name="title" label="文档标题" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="feishu_url" label="文档链接"><Input placeholder="https://..." /></Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
